import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Clock } from "lucide-react";

export const calcHours = (start, end, brk) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number), [eh, em] = end.split(":").map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm) - (Number(brk) || 0);
  return mins > 0 ? Math.round(mins / 60 * 100) / 100 : 0;
};
const hm = (iso) => (iso ? iso.slice(11, 16) : "");

export function ShiftFields({ value, onChange, compact }) {
  const { t } = useI18n();
  const set = (k, v) => onChange({ ...value, [k]: v });
  const hours = calcHours(value.start, value.end, value.break_minutes);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 min-[380px]:grid-cols-3 gap-2">
        <div>
          <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("shift_start")}</label>
          <Input data-testid="shift-start" type="time" value={value.start || ""} onChange={(e) => set("start", e.target.value)} className={compact ? "h-10" : "h-12 text-lg font-mono-num"} />
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("shift_end")}</label>
          <Input data-testid="shift-end" type="time" value={value.end || ""} onChange={(e) => set("end", e.target.value)} className={compact ? "h-10" : "h-12 text-lg font-mono-num"} />
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("break_min")}</label>
          <Input data-testid="shift-break" type="number" inputMode="numeric" min="0" value={value.break_minutes ?? ""} onChange={(e) => set("break_minutes", e.target.value)} className={compact ? "h-10" : "h-12 text-lg font-mono-num"} />
        </div>
      </div>
      <div className="text-sm text-[var(--text-3)]">{t("worked_hours")}: <b className="font-mono-num text-[var(--text)]" data-testid="shift-hours">{hours}h</b></div>
    </div>
  );
}

export default function ShiftCard({ date, projectId, onSaved }) {
  const { t } = useI18n();
  const [v, setV] = useState({ start: "", end: "", break_minutes: "" });
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/worktime/day?date=${date}`).then((r) => {
      setExisting(r.data);
      if (r.data) setV({ start: hm(r.data.start_time), end: hm(r.data.end_time), break_minutes: r.data.break_minutes ?? 0 });
      else setV({ start: "", end: "", break_minutes: "" });
    }).catch(() => {});
  }, [date]);

  const save = async () => {
    if (!v.start || !v.end) return toast.error(t("shift"));
    if (calcHours(v.start, v.end, v.break_minutes) <= 0) return toast.error(t("error"));
    setBusy(true);
    try {
      const { data } = await api.post("/worktime/manual", { date, project_id: projectId, start: v.start, end: v.end, break_minutes: Number(v.break_minutes) || 0 });
      setExisting(data); toast.success(`${t("shift_saved")}: ${data.hours}h`); onSaved?.(data);
    } catch (e) { toast.error(!navigator.onLine || !e.response ? t("offline_shift") : (e.response?.data?.detail || t("error"))); }
    finally { setBusy(false); }
  };

  return (
    <div className="tactical-card p-4 space-y-3" data-testid="shift-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[var(--text-3)] font-semibold flex items-center gap-1"><Clock size={13} /> {t("shift")}</div>
        {existing?.hours ? <span className="text-xs font-semibold text-[var(--green)]" data-testid="shift-saved-badge">✓ {existing.hours}h</span> : null}
      </div>
      <ShiftFields value={v} onChange={setV} />
      <Button data-testid="save-shift-btn" onClick={save} disabled={busy} className="w-full h-12 bg-[var(--sidebar)] hover:brightness-110 text-white font-bold rounded-xl">
        {t("save_shift")}
      </Button>
    </div>
  );
}
